"use client";

import { useEffect, useState } from "react";
import { Heart, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-1 py-2 text-foreground text-sm font-mono font-medium tracking-widest bg-transparent hover:bg-transparent focus:bg-transparent transition-colors uppercase">
                <span>{item.label}</span>
                <span aria-hidden="true" className="pointer-events-none absolute left-1/2 -bottom-0.5 h-px w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-36 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-44 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-32 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onNavigate} data-index={i} className="font-default text-3xl text-foreground font-bold tracking-tighter py-3 border-b border-border/60 hover:text-primary transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
export default function AcostasLandscaping() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="493" id="acostas-landscaping" className={`sticky top-0 left-0 right-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "bg-background/80 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center shrink-0">
          <Link to="/" aria-label="Acosta's Landscaping home" className="flex items-center">
            <WvcLogo className="h-10 w-auto max-h-10" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="16" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/GetQuotePage" className="hidden sm:inline-flex">
            <Button size="lg" className="rounded-md px-6 h-11 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 font-default font-bold uppercase tracking-wider">
              <Heart className="size-4 fill-current" />
              <span>Get Quote</span>
            </Button>
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="lg:hidden text-foreground hover:bg-accent rounded-md">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex items-center h-20 px-6 border-b border-border">
                <Link to="/" onClick={() => setOpen(false)} aria-label="Acosta's Landscaping home" className="flex items-center">
                  <WvcLogo className="h-10 w-auto max-h-10" />
                </Link>
              </div>

              <div className="flex-1 overflow-y-auto py-8">
                <MenuProvider menu_id="16" className="mobile-header-menu">
                  <MobileMenuConsumer onNavigate={() => setOpen(false)} />
                </MenuProvider>
              </div>

              <div className="px-6 pb-8 pt-4 border-t border-border">
                <Link to="/GetQuotePage" onClick={() => setOpen(false)}>
                  <Button size="lg" className="w-full rounded-md h-12 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-default font-bold uppercase tracking-wider">
                    <Heart className="size-4 fill-current" />
                    <span>Get Quote</span>
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}